// Compact play/pause button for list entries. All buttons share one Audio element, so starting
// one entry stops the previous one.
import { useEffect, useSyncExternalStore } from 'react';
import { audioUrl, type ViewClip } from '../lib/api';
import { localAudioUrl } from '../lib/outbox';
import { formatDuration } from '../lib/format';
import { Icon } from './Icon';
import { toast } from './Toasts';

interface PlaybackState {
  clipId: string | null;
  playing: boolean;
  loading: boolean;
}

let state: PlaybackState = { clipId: null, playing: false, loading: false };
let audio: HTMLAudioElement | null = null;
let objectUrl: string | null = null;
/** Bumped on every play/stop so a slow localAudioUrl() cannot start an outdated clip. */
let token = 0;
const listeners = new Set<() => void>();

function setState(patch: Partial<PlaybackState>): void {
  state = { ...state, ...patch };
  for (const listener of [...listeners]) listener();
}

function element(): HTMLAudioElement {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = 'auto';
  audio.addEventListener('playing', () => setState({ playing: true, loading: false }));
  audio.addEventListener('pause', () => setState({ playing: false, loading: false }));
  audio.addEventListener('ended', () => setState({ playing: false, loading: false }));
  audio.addEventListener('error', () => {
    if (!state.clipId) return;
    setState({ playing: false, loading: false });
    toast('Posnetka ni bilo mogoče predvajati.', { kind: 'error' });
  });
  return audio;
}

function releaseObjectUrl(): void {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = null;
}

export function stopPlayback(): void {
  token++;
  if (audio) {
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }
  releaseObjectUrl();
  setState({ clipId: null, playing: false, loading: false });
}

async function play(clip: ViewClip): Promise<void> {
  const el = element();
  if (state.clipId === clip.id && el.src) {
    if (el.paused) await el.play().catch(() => setState({ playing: false }));
    else el.pause();
    return;
  }
  const mine = ++token;
  el.pause();
  releaseObjectUrl();
  setState({ clipId: clip.id, playing: false, loading: true });
  let src: string | null;
  if (clip.localPending) {
    src = await localAudioUrl(clip.id).catch(() => null);
    if (mine !== token) {
      if (src) URL.revokeObjectURL(src);
      return;
    }
    objectUrl = src;
  } else {
    src = audioUrl(clip.id);
  }
  if (!src) {
    setState({ clipId: null, loading: false });
    toast('Posnetek še ni na voljo.', { kind: 'warning' });
    return;
  }
  el.src = src;
  try {
    await el.play();
  } catch (err) {
    if (mine !== token) return;
    setState({ playing: false, loading: false });
    // NotAllowedError = autoplay policy; anything else is a decoding/network problem.
    if ((err as { name?: string }).name !== 'AbortError') toast('Posnetka ni bilo mogoče predvajati.', { kind: 'error' });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getState = () => state;

export function usePlaybackState(): PlaybackState {
  return useSyncExternalStore(subscribe, getState, getState);
}

/** Stops shared playback when the calling page unmounts. */
export function useStopPlaybackOnUnmount(): void {
  useEffect(() => () => stopPlayback(), []);
}

export function PlayButton({ clip, label }: { clip: ViewClip; label: string }) {
  const playback = usePlaybackState();
  if (!clip.hasAudio) return null;
  const mine = playback.clipId === clip.id;
  const playing = mine && playback.playing;
  const loading = mine && playback.loading;
  return (
    <button
      type="button"
      className={`play-btn${playing ? ' is-playing' : ''}`}
      aria-label={playing ? `Ustavi predvajanje: ${label}` : `Predvajaj: ${label}`}
      aria-pressed={playing}
      onClick={() => void play(clip)}
    >
      <Icon name={loading ? 'spinner' : playing ? 'pause' : 'play'} size={18} />
      {clip.durationMs ? <span>{formatDuration(clip.durationMs)}</span> : null}
    </button>
  );
}
